import React from 'react';
import PropTypes from 'prop-types';
import { ListItem, ListItemText } from '@material-ui/core';
import { isEmpty } from 'lodash';
import i18n from 'i18n-js';
import * as Yup from 'yup';
import { Field, Form, Formik } from 'formik';
import { SwitchField, TextField } from 'components/FormFields';

const BrokerSettingsForm = ({
  brokerSettings,
  formRef,
  listClasses,
  listItemsClasses,
  onSubmit,
}) => {
  /**
   * Builds a Yup validation schema based on the broker settings schema.
   * @returns {Yup.ObjectSchema} The Yup validation schema.
   */
  function buildYupSchema() {
    const schemaObject = {};
    for (const key in brokerSettings?.schema?.properties) {
      const field = brokerSettings.schema.properties[key];
      if (field.disabled) continue;

      let fieldSchema;
      switch (field.type) {
        case 'string':
          fieldSchema = Yup.string();
          break;
        case 'email':
          fieldSchema = Yup.string().email(i18n.t('common.invalid_email'));
          break;
        case 'integer':
          fieldSchema = Yup.number().integer().min(field.min).max(field.max);
          break;
        case 'boolean':
          fieldSchema = Yup.boolean();
          break;
        default:
          continue;
      }

      if (field.required) {
        fieldSchema = fieldSchema.required(i18n.t('common.required_field'));
      }
      schemaObject[key] = fieldSchema;
    }
    return Yup.object().shape(schemaObject);
  }

  /**
   * Returns the appropriate form field component based on the field type.
   * @param {string} key - The name of the field.
   * @param {string} type - The type of the field.
   * @returns {JSX.Element} The form field component.
   */
  const getField = (key, type) => {
    switch (type) {
      case 'boolean':
        return <Field name={key} color="primary" component={SwitchField} />;
      case 'string':
        return <Field name={key} component={TextField} />;
      case 'email':
        return <Field name={key} type="email" component={TextField} />;
      case 'integer':
        return <Field name={key} type="number" component={TextField} />;
      default:
        return null;
    }
  };

  return !isEmpty(brokerSettings?.schema) ? (
    <Formik
      enableReinitialize
      innerRef={formRef}
      onSubmit={onSubmit}
      initialValues={brokerSettings.payload}
      validationSchema={buildYupSchema()}
    >
      {({ handleSubmit }) => (
        <Form noValidate onSubmit={handleSubmit}>
          {Object.keys(brokerSettings?.schema?.properties)?.map((key) => {
            const field = brokerSettings.schema.properties[key];
            if (field.disabled) {
              return null;
            }
            return (
              <ListItem key={key} classes={listItemsClasses}>
                <ListItemText
                  classes={listClasses}
                  primary={field.title}
                  secondary={getField(key, field.type)}
                />
              </ListItem>
            );
          })}
        </Form>
      )}
    </Formik>
  ) : null;
};

BrokerSettingsForm.propTypes = {
  brokerSettings: PropTypes.shape({
    schema: PropTypes.shape({
      properties: PropTypes.object,
    }),
    payload: PropTypes.object,
  }).isRequired,
  formRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  listClasses: PropTypes.object,
  listItemsClasses: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
};

export default BrokerSettingsForm;
